/**
 * Email verification modal — shown when a free unverified user hits the verification gate.
 * Auto-send triggers before opening; this modal confirms the email was sent and provides resend.
 */

import { Mail } from "lucide-react";
import { create } from "zustand";
import { ResendVerificationButton } from "@/components/resend-verification-button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/stores/auth-store";

interface VerificationModalState {
	isOpen: boolean;
	runsRemaining: number;
	emailSent: boolean;
	open: (runsRemaining: number, emailSent: boolean) => void;
	close: () => void;
}

export const useVerificationModalStore = create<VerificationModalState>((set) => ({
	isOpen: false,
	runsRemaining: 0,
	emailSent: false,
	open: (runsRemaining, emailSent) => set({ isOpen: true, runsRemaining, emailSent }),
	close: () => set({ isOpen: false }),
}));

export function EmailVerificationModal() {
	const { isOpen, runsRemaining, emailSent, close } = useVerificationModalStore();
	const email = useAuthStore((s) => s.user?.email);

	if (!email) return null;

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
			<DialogContent className="max-w-md">
				<DialogHeader className="items-center text-center">
					<div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
						<Mail className="size-6 text-blue-600 dark:text-blue-300" />
					</div>
					<DialogTitle>Check your email</DialogTitle>
					<DialogDescription>
						{emailSent ? (
							<>
								We've sent a verification email to <strong>{email}</strong>
							</>
						) : (
							<>
								A verification email was recently sent to <strong>{email}</strong>
							</>
						)}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 text-center text-sm">
					<p className="text-muted-foreground">
						You have <strong>{runsRemaining}</strong> free {runsRemaining === 1 ? "run" : "runs"}{" "}
						remaining after verifying.
					</p>
					<ResendVerificationButton email={email} variant="outline" className="w-full" />
					<p className="text-xs text-muted-foreground">Check your inbox, including spam folder</p>
				</div>
			</DialogContent>
		</Dialog>
	);
}
